import { BRAND } from './brand-ramp';

import type { LucideIcon } from 'lucide-react';
import type { ReactElement } from 'react';

/**
 * The icon medallion — a rounded tile carrying one line-icon, tinted by a
 * position on the brand ramp.
 *
 * ## Why a primitive rather than a class at each call site
 *
 * The site had no icon vocabulary at all. Every list of capabilities was a
 * white card with a bold line and a grey paragraph, fourteen of them in a row,
 * and the result read as a specification rather than a product: correct,
 * legible, and completely undifferentiated. An icon is the cheapest way to give
 * a scanning reader a fixed point per item, and a TINTED icon is the cheapest
 * way to get brand colour onto a light page without painting a headline in it.
 *
 * It is a primitive because the alternative is four call sites each inventing
 * their own tile size, radius, tint strength and icon stroke — the exact drift
 * `panel-classes.ts` was extracted to stop.
 *
 * ## The tint is a GROUND, so the icon is measured against it
 *
 * `hue` names a ramp stop; the tile is painted with that stop at 12% over the
 * page and the glyph is drawn in the stop's INK variant. A line icon is a
 * non-text contrast case (WCAG 1.4.11, 3:1), and `medallion-contrast.test.ts`
 * measures every hue's glyph against its own tile rather than against white —
 * a tinted ground is darker than the page, which is precisely where a ratio
 * derived from white misleads (`bedrock-contrast.test.ts` § FILL/INK).
 */

/**
 * Positions a medallion may take. Deliberately closed.
 *
 * Four identity stops plus three STATUS tones. The status half was added for
 * `/status`, where the verdict mark was drawn in `violet` for both `degraded`
 * and `outage` — a purple triangle announcing a service disruption, which is
 * not merely off-vocabulary but actively misinforms: purple is the brand, and
 * the one page a visitor opens while something of theirs is failing is the last
 * place a mark should be decorative. The status tones are ordinary members
 * rather than a second component, so every gate below measures them too.
 */
export type MedallionHue = 'cyan' | 'blue' | 'violet' | 'purple' | 'ok' | 'warn' | 'danger';

/**
 * Glyph inks, one per hue.
 *
 * These are NOT the ramp stops. `--bd-cyan` is 1.80:1 on white and worse on its
 * own tint, so drawing the glyph in the fill colour would produce an icon that
 * is legible only as a smudge. Each value is the darkest-but-still-hued member
 * of its family that clears the 3:1 non-text floor on its own tile, measured in
 * the sibling test.
 */
const INK: Record<MedallionHue, string> = {
  cyan: '#0b6f93',
  blue: '#2348db',
  violet: '#a90fd2',
  purple: '#6b14cc',
  // The status inks are `bedrock.css`'s `--bd-*-ink` values, transcribed rather
  // than referenced because this table is also read by the contrast test, which
  // cannot resolve a `var()`. `bedrock-contrast.test.ts` measures them at their
  // source; here they are measured again on the tile they actually sit on.
  ok: '#0f766e',
  warn: '#8a5a06',
  danger: '#b01c1c',
};

const FILL: Record<MedallionHue, string> = {
  cyan: BRAND.cyan,
  blue: BRAND.blue,
  violet: BRAND.violet,
  purple: BRAND.purple,
  ok: '#34d399',
  warn: '#f59e0b',
  danger: '#dc2626',
};

/**
 * Glyph inks on a dark band — a second table rather than "use the ramp stop".
 *
 * That shortcut was the first version and the test refused it: on a band the
 * tile is the stop at 18% over `--bd-band`, so BOTH sides of the ratio move
 * with the hue, and `--bd-purple` on its own purple tile measures **2.71:1** —
 * under the 3:1 non-text floor. Blue (3.53) and violet (3.51) scraped past,
 * which is the more dangerous outcome of the two: a reviewer sampling one hue
 * would have concluded the rule held.
 *
 * Each value is a lightened member of its own family, so the medallion still
 * reads as that hue on a band; all four clear 7:1 there.
 */
const BAND_INK: Record<MedallionHue, string> = {
  cyan: BRAND.cyan,
  blue: '#93a9ff',
  violet: '#eb9bfa',
  purple: '#c39bff',
  // The status fills repeat the same story: `--bd-danger` on its own 18% tile
  // measures 3.66:1, which passes the floor and still reads as a dull maroon
  // smudge beside its siblings. Lightened in-family, all three clear 6:1.
  ok: '#6ee7b7',
  warn: '#fbbf24',
  danger: '#f87171',
};

export interface MedallionProps {
  readonly icon: LucideIcon;
  readonly hue: MedallionHue;
  /** Paint for a dark band ground rather than the light page. */
  readonly onDark?: boolean;
  /** `sm` for a dense row, `md` for a card header. */
  readonly size?: 'sm' | 'md';
  readonly className?: string;
}

export function Medallion({
  icon: Icon,
  hue,
  onDark = false,
  size = 'md',
  className,
}: MedallionProps): ReactElement {
  const box = size === 'sm' ? 'size-8 rounded-[10px]' : 'size-11 rounded-[14px]';
  const glyph = size === 'sm' ? 'size-4' : 'size-[1.35rem]';
  return (
    <span
      aria-hidden="true"
      className={['grid flex-none place-items-center', box, className].filter(Boolean).join(' ')}
      style={{
        // On a dark band the same 12% tint would vanish into the ground, so the
        // fill is lifted — and because that moves the ground the glyph sits on,
        // the ink moves with it (see `BAND_INK`).
        background: onDark
          ? `color-mix(in srgb, ${FILL[hue]} 18%, transparent)`
          : `color-mix(in srgb, ${FILL[hue]} 12%, var(--bd-page))`,
        boxShadow: onDark
          ? `inset 0 0 0 1px color-mix(in srgb, ${FILL[hue]} 28%, transparent)`
          : `inset 0 0 0 1px color-mix(in srgb, ${FILL[hue]} 22%, transparent)`,
        color: onDark ? BAND_INK[hue] : INK[hue],
      }}
    >
      <Icon className={glyph} strokeWidth={1.75} />
    </span>
  );
}

/** The ink a hue draws its glyph in on a LIGHT ground — exported so the
 *  contrast test measures the shipped value rather than a second
 *  transcription of it. */
export const MEDALLION_INK: Readonly<Record<MedallionHue, string>> = INK;
/** The same, on a dark band. */
export const MEDALLION_BAND_INK: Readonly<Record<MedallionHue, string>> = BAND_INK;
/** The fill a hue tints its tile with. Same reason. */
export const MEDALLION_FILL: Readonly<Record<MedallionHue, string>> = FILL;
/** The tint strengths the tile is mixed at, per ground — so the test computes
 *  the composite from the shipped numbers rather than repeating them. */
export const MEDALLION_TINT = { light: 0.12, dark: 0.18 } as const;
